/**
 * 自适应网络管理器
 * 解决网络波动导致的接口500错误问题
 * 提供智能重试和网络状态检测
 */

export interface NetworkInfo {
  quality: 'slow' | 'normal' | 'fast'
  connectionType: string
  isOnline: boolean
  lastUpdate: number
}

export interface AdaptiveConfig {
  timeout: number
  maxRetries: number
  retryDelay: number
  enableRetry: boolean
}

class AdaptiveNetworkManager {
  private networkInfo: NetworkInfo = {
    quality: 'normal',
    connectionType: 'unknown',
    isOnline: navigator.onLine,
    lastUpdate: Date.now()
  }

  private configListeners: Array<(config: AdaptiveConfig) => void> = []
  private retryCount = 0
  private lastErrorTime = 0

  constructor() {
    // 延迟初始化，避免影响应用启动
    setTimeout(() => {
      this.initialize()
    }, 1000)
  }

  private async initialize(): Promise<void> {
    try {
      await this.detectNetworkQuality()
      this.setupNetworkListeners()
      this.adjustHttpConfig()
    } catch (error) {
      console.warn('网络管理器初始化失败:', error)
    }
  }

  /**
   * 检测网络质量
   */
  private async detectNetworkQuality(): Promise<void> {
    try {
      // 检测连接类型
      if ('connection' in navigator && navigator.connection) {
        const connection = (navigator as any).connection
        this.networkInfo.connectionType = connection.effectiveType || 'unknown'

        // 根据连接类型判断网络质量
        switch (this.networkInfo.connectionType) {
          case 'slow-2g':
          case '2g':
            this.networkInfo.quality = 'slow'
            break
          case '3g':
            this.networkInfo.quality = 'normal'
            break
          case '4g':
          case '5g':
            this.networkInfo.quality = 'fast'
            break
          default:
            this.networkInfo.quality = 'normal'
        }
      }

      this.networkInfo.lastUpdate = Date.now()
    } catch (error) {
      console.warn('网络质量检测失败:', error)
    }
  }

  /**
   * 设置网络状态监听器
   */
  private setupNetworkListeners(): void {
    try {
      // 监听网络连接变化
      if ('connection' in navigator && navigator.connection) {
        const connection = (navigator as any).connection
        connection.addEventListener('change', () => {
          console.log('🌐 网络连接状态变化')
          this.detectNetworkQuality()
          this.adjustHttpConfig()
        })
      }

      // 监听在线状态变化
      window.addEventListener('online', () => {
        console.log('🌐 网络已恢复在线')
        this.networkInfo.isOnline = true
        this.detectNetworkQuality()
        this.adjustHttpConfig()
      })

      window.addEventListener('offline', () => {
        console.log('🌐 网络已断开')
        this.networkInfo.isOnline = false
        this.networkInfo.quality = 'slow'
        this.adjustHttpConfig()
      })

    } catch (error) {
      console.warn('设置网络监听器失败:', error)
    }
  }

  /**
   * 根据网络质量和错误情况获取自适应配置
   */
  private getAdaptiveConfig(): AdaptiveConfig {
    const baseConfig = {
      timeout: 30000,
      maxRetries: 2,
      retryDelay: 1000,
      enableRetry: true
    }

    const now = Date.now()
    const timeSinceLastError = now - this.lastErrorTime

    // 如果最近有错误，增加重试次数和延迟
    if (timeSinceLastError < 60000 && this.retryCount > 0) { // 1分钟内有错误
      return {
        ...baseConfig,
        maxRetries: Math.min(5, baseConfig.maxRetries + this.retryCount), // 最多重试5次
        retryDelay: Math.min(5000, baseConfig.retryDelay * Math.pow(2, this.retryCount)), // 指数退避，最多5秒
        enableRetry: true
      }
    }

    // 根据网络质量调整配置
    switch (this.networkInfo.quality) {
      case 'slow':
        return {
          ...baseConfig,
          timeout: 45000, // 更长的超时时间
          maxRetries: 4,  // 增加重试次数
          retryDelay: 2000, // 更长的重试延迟
          enableRetry: true
        }

      case 'fast':
        return {
          ...baseConfig,
          timeout: 15000, // 更短的超时时间
          maxRetries: 1,  // 减少重试次数
          retryDelay: 500, // 更短的重试延迟
          enableRetry: true
        }

      default: // normal
        return baseConfig
    }
  }

  /**
   * 调整HTTP客户端配置
   */
  private adjustHttpConfig(): void {
    try {
      const adaptiveConfig = this.getAdaptiveConfig()

      // 通知所有监听器配置变更
      this.configListeners.forEach(listener => {
        try {
          listener(adaptiveConfig)
        } catch (error) {
          console.warn('配置监听器执行失败:', error)
        }
      })

    } catch (error) {
      console.warn('调整HTTP配置失败:', error)
    }
  }

  /**
   * 记录网络错误，用于调整重试策略
   */
  recordNetworkError(): void {
    this.retryCount = Math.min(10, this.retryCount + 1)
    this.lastErrorTime = Date.now()
    this.adjustHttpConfig()
  }

  /**
   * 记录网络成功，重置错误计数
   */
  recordNetworkSuccess(): void {
    if (this.retryCount > 0) {
      this.retryCount = Math.max(0, this.retryCount - 1)
    }
  }

  /**
   * 添加配置变更监听器
   */
  addConfigListener(listener: (config: AdaptiveConfig) => void): void {
    this.configListeners.push(listener)
  }

  /**
   * 移除配置变更监听器
   */
  removeConfigListener(listener: (config: AdaptiveConfig) => void): void {
    const index = this.configListeners.indexOf(listener)
    if (index > -1) {
      this.configListeners.splice(index, 1)
    }
  }

  /**
   * 获取当前网络信息
   */
  getNetworkInfo(): NetworkInfo {
    return { ...this.networkInfo }
  }

  /**
   * 获取当前自适应配置
   */
  getCurrentAdaptiveConfig(): AdaptiveConfig {
    return this.getAdaptiveConfig()
  }

  /**
   * 手动刷新网络检测
   */
  async refreshNetworkInfo(): Promise<void> {
    await this.detectNetworkQuality()
    this.adjustHttpConfig()
  }

  /**
   * 销毁管理器
   */
  destroy(): void {
    this.configListeners.length = 0
  }
}

// 创建单例实例
let adaptiveNetworkManager: AdaptiveNetworkManager | null = null

/**
 * 获取自适应网络管理器实例
 */
export function getAdaptiveNetworkManager(): AdaptiveNetworkManager {
  if (!adaptiveNetworkManager) {
    adaptiveNetworkManager = new AdaptiveNetworkManager()
  }
  return adaptiveNetworkManager
}

/**
 * 获取当前网络信息
 */
export function getCurrentNetworkInfo(): NetworkInfo {
  const manager = getAdaptiveNetworkManager()
  return manager.getNetworkInfo()
}

/**
 * 记录网络错误
 */
export function recordNetworkError(): void {
  const manager = getAdaptiveNetworkManager()
  manager.recordNetworkError()
}

/**
 * 记录网络成功
 */
export function recordNetworkSuccess(): void {
  const manager = getAdaptiveNetworkManager()
  manager.recordNetworkSuccess()
}

// 默认导出单例
export default getAdaptiveNetworkManager()